import { Assets, Graphics, Texture, type Renderer } from 'pixi.js';

/**
 * The icon set.
 *
 * **Real art goes in `packages/client/public/icons/`** -- one file per icon,
 * named after it, SVG or PNG. See the README there for sizing and color. Drop
 * files in and they are used; nothing here needs editing.
 *
 * The shapes below are what the game draws when a file is missing. They are a
 * placeholder so the map is never blank and so the set can arrive one icon at a
 * time, not a design anybody should have to live with.
 *
 * They tint: one flat shape per icon, coloured per instance, so a pickup and a
 * drop are the same texture in two colors. Anything dropped in should be a
 * single color on transparency for the same reason.
 */

/** Everything the map needs to point at. */
export type IconName =
  | 'pharmacy'
  | 'grocery'
  | 'hardware'
  | 'garage'
  | 'depot'
  | 'storage'
  | 'diner'
  | 'bar'
  | 'clinic'
  | 'office'
  | 'yard'
  | 'garden'
  | 'pickup'
  | 'drop'
  | 'camera'
  | 'dispatch'
  | 'fuel'
  | 'disc'
  | 'balloons'
  | 'bag'
  | 'tape'
  | 'paint';

const SIZE = 24;
const STROKE = 2.6;

type Draw = (g: Graphics) => void;

/**
 * How each one is drawn.
 *
 * Read as instructions rather than art: a cross for a pharmacy, a fuel pump for
 * a garage, a roller shutter for storage. Nothing here is trying to be clever,
 * because an icon somebody has to decode is worse than a word.
 */
const SHAPES: Readonly<Record<IconName, Draw>> = Object.freeze({
  /**
   * A filling station: a pump, with the hose and the nozzle on the side.
   *
   * There was no fuel icon at all, which is why nothing appeared on the map
   * however hard anybody looked -- and the fallback pin never ran either,
   * because nothing was drawing fuel venues in the first place.
   */
  fuel: (g) => {
    // The pump body.
    g.roundRect(4, 4, 10, 17, 1.6).fill(0xffffff);
    // Its display panel, cut out so the body reads as a machine.
    g.rect(6.2, 6.4, 5.6, 4).cut();
    // The hose up the side and the nozzle over the top.
    g.moveTo(14, 9)
      .lineTo(17.6, 9)
      .lineTo(17.6, 16.5)
      .stroke({ width: 1.8, color: 0xffffff });
    g.circle(17.6, 17.6, 1.6).fill(0xffffff);
    g.roundRect(15.4, 5.6, 4.4, 2.4, 1).fill(0xffffff);
  },
  // A cross. The one icon nobody has to be taught.
  pharmacy: (g) => {
    g.rect(9.5, 3, 5, 18).fill(0xffffff);
    g.rect(3, 9.5, 18, 5).fill(0xffffff);
  },
  // A basket.
  grocery: (g) => {
    g.moveTo(3, 8).lineTo(21, 8).lineTo(18, 20).lineTo(6, 20).closePath().stroke({ width: STROKE, color: 0xffffff });
    g.moveTo(8, 8).lineTo(10, 3).stroke({ width: STROKE, color: 0xffffff });
    g.moveTo(16, 8).lineTo(14, 3).stroke({ width: STROKE, color: 0xffffff });
  },
  // A hammer.
  hardware: (g) => {
    g.rect(4, 5, 11, 5).fill(0xffffff);
    g.rect(11, 8, 3.4, 13).fill(0xffffff);
  },
  // A fuel pump.
  garage: (g) => {
    g.rect(4, 5, 10, 16).stroke({ width: STROKE, color: 0xffffff });
    g.rect(6.5, 8, 5, 4).fill(0xffffff);
    g.moveTo(14, 9).lineTo(19, 9).lineTo(19, 17).stroke({ width: STROKE, color: 0xffffff });
  },
  // A crate on a pallet.
  depot: (g) => {
    g.rect(4, 6, 16, 10).stroke({ width: STROKE, color: 0xffffff });
    g.moveTo(4, 11).lineTo(20, 11).stroke({ width: STROKE, color: 0xffffff });
    g.rect(3, 18, 18, 3).fill(0xffffff);
  },
  // A roller shutter.
  storage: (g) => {
    g.rect(4, 4, 16, 16).stroke({ width: STROKE, color: 0xffffff });
    for (const y of [8, 11.5, 15]) g.moveTo(6, y).lineTo(18, y).stroke({ width: 1.8, color: 0xffffff });
  },
  // A mug.
  diner: (g) => {
    g.rect(4, 7, 12, 12).stroke({ width: STROKE, color: 0xffffff });
    g.moveTo(16, 10).lineTo(20, 10).lineTo(20, 15).lineTo(16, 15).stroke({ width: STROKE, color: 0xffffff });
  },
  // A glass.
  bar: (g) => {
    g.moveTo(6, 4).lineTo(18, 4).lineTo(13, 13).lineTo(13, 20).stroke({ width: STROKE, color: 0xffffff });
    g.moveTo(9, 20).lineTo(17, 20).stroke({ width: STROKE, color: 0xffffff });
  },
  // A cross in a circle, which reads as care rather than pharmacy.
  clinic: (g) => {
    g.circle(12, 12, 9).stroke({ width: STROKE, color: 0xffffff });
    g.rect(10.8, 7, 2.4, 10).fill(0xffffff);
    g.rect(7, 10.8, 10, 2.4).fill(0xffffff);
  },
  // A building with windows.
  office: (g) => {
    g.rect(5, 3, 14, 18).stroke({ width: STROKE, color: 0xffffff });
    for (const y of [7, 12]) for (const x of [8, 14]) g.rect(x, y, 3, 3).fill(0xffffff);
  },
  // A crushed shape, for salvage.
  yard: (g) => {
    g.moveTo(3, 18).lineTo(7, 9).lineTo(13, 13).lineTo(17, 6).lineTo(21, 18).closePath()
      .stroke({ width: STROKE, color: 0xffffff });
  },
  // A leaf.
  garden: (g) => {
    g.moveTo(5, 19).bezierCurveTo(5, 8, 14, 4, 20, 5).bezierCurveTo(20, 15, 12, 20, 5, 19)
      .stroke({ width: STROKE, color: 0xffffff });
    g.moveTo(5, 19).lineTo(15, 10).stroke({ width: 1.8, color: 0xffffff });
  },
  // A pin, pointing down at where the thing is.
  pickup: (g) => {
    g.moveTo(12, 22).lineTo(5, 10).arc(12, 8, 7.3, Math.PI, 0).closePath()
      .stroke({ width: STROKE, color: 0xffffff });
    g.circle(12, 8.5, 2.6).fill(0xffffff);
  },
  // The same pin, filled, because a destination is not a suggestion.
  drop: (g) => {
    g.moveTo(12, 22).lineTo(5, 10).arc(12, 8, 7.3, Math.PI, 0).closePath().fill(0xffffff);
  },
  // A box on a pole. This is what one actually looks like.
  /**
   * What is over the lens, when something is.
   *
   * Small marks, drawn where the scanner ray used to be. A covered camera has
   * no ray -- it is not reading anything -- and leaving the space empty says
   * only that something is wrong. These say what.
   *
   * Balloons because it is a real tactic and a documented one; people have tied
   * them to these. It reads as exactly what it is at eight pixels, which is
   * more than can be said for most of what has been tried on this map.
   */
  balloons: (g) => {
    g.circle(8, 8, 5).fill(0xffffff);
    g.circle(16.5, 7, 4.4).fill(0xffffff);
    g.circle(12, 15, 4.6).fill(0xffffff);
    g.moveTo(8, 13).lineTo(11, 22).stroke({ width: 1.4, color: 0xffffff });
    g.moveTo(16.5, 11.4).lineTo(13, 22).stroke({ width: 1.4, color: 0xffffff });
  },
  /** A sack over the housing, tied at the neck. */
  bag: (g) => {
    g.moveTo(6, 21).lineTo(7.5, 9).lineTo(16.5, 9).lineTo(18, 21).closePath().fill(0xffffff);
    g.rect(9.5, 4, 5, 5).fill(0xffffff);
  },
  /** A strip across the glass. */
  tape: (g) => {
    g.moveTo(3, 15).lineTo(19, 6).lineTo(21, 10).lineTo(5, 19).closePath().fill(0xffffff);
  },
  /** A can, and what came out of it. */
  paint: (g) => {
    g.roundRect(7, 9, 8, 13, 1.6).fill(0xffffff);
    g.rect(9, 5, 4, 4).fill(0xffffff);
    g.circle(17.5, 6, 1.5).fill(0xffffff);
    g.circle(20.5, 9, 1.2).fill(0xffffff);
  },
  /**
   * A plain filled circle, drawn dark behind another icon.
   *
   * Map icons have to read on whatever is underneath them, and once aerial
   * imagery is switched on that is a photograph: pale concrete, bright roofs,
   * a field in full sun. An orange glyph on a dark map is legible and the same
   * glyph on a car park is not there at all.
   *
   * Every mapping product solves this the same way and so does this one -- put
   * the mark on something. A dark disc behind the glyph gives every icon its
   * own background, so legibility stops depending on the ground.
   */
  disc: (g) => {
    g.circle(12, 12, 12).fill(0xffffff);
  },
  /**
   * A camera on the map, **seen from above**, looking up the screen.
   *
   * Two earlier attempts are worth recording because both failed the same way.
   * A side elevation -- a pole with a box on it -- is wrong for a plan view: at
   * map sizes the pole vanished and what was left read as a thick letter T, and
   * a side view has no heading, so the direction cone had nothing to attach to
   * and floated below it. Then a literal housing drawn from above, with a body
   * and a lens hood and the glass punched out, turned to a lumpy orange blob at
   * forty pixels -- overlapping fills and a cut-out do not survive being small.
   *
   * So this is not a drawing of a camera. It is the mark a plan uses for one,
   * which is what every security drawing and every street-view map converges
   * on for the same reason: **a mount, and a lens on the front of it.** Two
   * shapes, no interior detail, unmistakable at eighteen pixels, and it has a
   * front for the cone to come out of.
   *
   * Drawn facing up because zero degrees is north and Pixi turns clockwise,
   * which is what a compass bearing already is.
   */
  camera: (g) => {
    // The mount, which is the thing standing at the recorded position.
    g.circle(12, 13.4, 5).fill(0xffffff);
    // The lens on the front of it, square-on so the facing is unambiguous.
    g.roundRect(9.2, 4.6, 5.6, 6, 1).fill(0xffffff);
  },
  /**
   * A camera that has stopped reporting, which is all a console ever knows.
   *
   * The same mark, hollow. A covered lens, a fault, a dead backhaul and a
   * stolen unit are indistinguishable from a terminal, so this says only that
   * nothing is coming from it -- an empty outline where a solid one was.
   */
  dispatch: (g) => {
    g.circle(12, 13.4, 4.4).stroke({ width: 2.2, color: 0xffffff });
    g.roundRect(9.4, 5.2, 5.2, 5.4, 1).stroke({ width: 2, color: 0xffffff });
  },
});

/** Every icon the game asks for, in one place. */
export const ICON_NAMES = Object.keys(SHAPES) as IconName[];

/**
 * Bakes every icon once, at boot, and replaces any that ship as files.
 *
 * The drawn shapes above are a **fallback**, not the intended set. Drop a file
 * into `public/icons/` named after the icon -- `pharmacy.svg`, `garage.png` --
 * and it is used instead, with no code change and no rebuild step. A partial
 * set is fine: whatever is missing keeps the drawn version, so the art can
 * arrive one icon at a time.
 *
 * Files are loaded after the fallbacks are baked, so the game is never waiting
 * on them and a missing or broken file costs nothing but its own icon.
 *
 * Textures rather than live Graphics because the map may draw hundreds of these
 * and a texture is one quad. Resolution 3 so they stay sharp when a phone with
 * a dense screen scales them up.
 */
export class Icons {
  private readonly textures = new Map<IconName, Texture>();
  /** Which ones came from a file, for anybody checking what is actually in use. */
  readonly fromFile = new Set<IconName>();

  constructor(renderer: Renderer) {
    for (const [name, draw] of Object.entries(SHAPES) as [IconName, Draw][]) {
      const shape = new Graphics();
      draw(shape);
      this.textures.set(name, renderer.generateTexture({ target: shape, resolution: 3 }));
      shape.destroy();
    }
    void this.loadFiles();
  }

  /**
   * Swap in anything that exists on disk.
   *
   * Tries SVG first because it scales, then PNG. A 404 is the normal case for
   * most of the set and must be silent -- this is an optional upgrade, not a
   * missing dependency.
   */
  private async loadFiles(): Promise<void> {
    await Promise.all(
      ICON_NAMES.map(async (name) => {
        for (const ext of ['svg', 'png']) {
          const url = `/icons/${name}.${ext}`;
          try {
            const head = await fetch(url, { method: 'HEAD' });
            if (!head.ok) continue;
            const texture = await Assets.load<Texture>(url);
            if (texture) {
              this.textures.set(name, texture);
              this.fromFile.add(name);
            }
            return;
          } catch {
            // No such file, or it is not an image. The drawn one stands.
          }
        }
      }),
    );
  }

  get(name: IconName): Texture {
    const texture = this.textures.get(name);
    if (!texture) throw new Error(`no icon named ${name}`);
    return texture;
  }

  /** Whether a string names an icon, for venue kinds arriving off the wire. */
  has(name: string): name is IconName {
    return this.textures.has(name as IconName);
  }

  /** The size the shapes were drawn at, so callers can scale from it. */
  get unit(): number {
    return SIZE;
  }
}
